import { ConveyancingCompletionAlpha } from './ConveyancingCompletionAlpha';
import { DateOnlyInput } from '../components/DateOnlyInput';
import { alphaSave } from '../ui/alpha-save';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router';
import { api, authenticatedFetch } from '../api';
import { ErrorState, Loading } from '../ui/State';
type Answer = {
  question: string;
  answer: string;
  documentId: string;
  versionId: string;
  verifiedOn: string;
};
type Model = {
  case: {
    id: string;
    title: string;
    stage: string | null;
    version: number;
    fee_status: string;
    answers: Answer[];
  };
  documents: { id: string; versionId: string; title: string }[];
  history: {
    id: string;
    event_type: string;
    created_at: string;
    detail: { note?: string; answers?: Answer[]; version?: number };
  }[];
};
export function ConveyancingAlpha() {
  const [q] = useSearchParams(),
    building = q.get('building');
  const [list, setList] = useState<{
      cases: { id: string; title: string; stage: string | null }[];
      premises: { id: string; display_name: string }[];
    }>(),
    [selected, setSelected] = useState(''),
    [error, setError] = useState<unknown>();
  useEffect(() => {
    setList(undefined);
    setSelected('');
    if (!building) return;
    let active = true;
    api<any>(`/api/alpha/conveyancing?building=${building}`)
      .then((r) => {
        if (active) setList(r);
      })
      .catch((e) => {
        if (active) setError(e);
      });
    return () => {
      active = false;
    };
  }, [building]);
  return (
    <>
      <h1>Conveyancing</h1>
      <p>Prepare a source-backed sales pack or certificate from an existing correspondence case.</p>
      {Boolean(error) && <ErrorState error={error} />}{' '}
      {!building ? (
        <p>Open conveyancing from a building.</p>
      ) : !list ? (
        <Loading />
      ) : (
        <>
          <label>
            Correspondence case
            <select value={selected} onChange={(e) => setSelected(e.target.value)}>
              <option value="">Choose a case</option>
              {list.cases.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.title}
                  {c.stage ? ` · ${c.stage}` : ''}
                </option>
              ))}
            </select>
          </label>
          {!list.cases.length && (
            <p>
              No correspondence cases in this building. Record the incoming enquiry in
              Communications first.
            </p>
          )}
          {selected && <CaseEditor key={selected} id={selected} premises={list.premises} />}
        </>
      )}
    </>
  );
}
function CaseEditor({
  id,
  premises,
}: {
  id: string;
  premises: { id: string; display_name: string }[];
}) {
  const [model, setModel] = useState<Model>(),
    [answers, setAnswers] = useState<Answer[]>([]),
    [fee, setFee] = useState('Unconfirmed'),
    [error, setError] = useState<unknown>(),
    [busy, setBusy] = useState(false),
    [notice, setNotice] = useState('');
  const pending = useRef<{ path: string; body: unknown } | null>(null),
    root = '/api/alpha/conveyancing/' + id;
  const load = async () => {
    const r = await api<Model>(root);
    setModel(r);
    setAnswers(r.case.answers ?? []);
    setFee(r.case.fee_status ?? 'Unconfirmed');
  };
  useEffect(() => {
    void load().catch(setError);
  }, [id]);
  async function write(path: string, body: object) {
    setBusy(true);
    setError(undefined);
    pending.current ??= { path, body: { ...body, idempotencyKey: crypto.randomUUID() } };
    try {
      await alphaSave(pending, root + pending.current.path, {
        method: 'POST',
        body: JSON.stringify(pending.current.body),
      });
      await load();
      pending.current = null;
      setNotice('Saved. The current case has been refreshed.');
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }
  function start(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    void write('/start', { premisesId: f.get('premises'), requestKind: f.get('kind') });
  }
  async function download(pack: string) {
    try {
      const r = await authenticatedFetch(root + '/packs/' + pack);
      if (!r.ok) throw Error('Pack download unavailable');
      const url = URL.createObjectURL(await r.blob()),
        a = document.createElement('a');
      a.href = url;
      a.download = 'conveyancing-pack.html';
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      setError(e);
    }
  }
  function update(index: number, patch: Partial<Answer>) {
    setAnswers((a) => a.map((item, i) => (i === index ? { ...item, ...patch } : item)));
  }
  if (!model) return <>{Boolean(error) ? <ErrorState error={error} /> : <Loading />}</>;
  const locked = busy || !!pending.current;
  return (
    <section className="panel">
      <h2>{model.case.title}</h2>
      {Boolean(error) && <ErrorState error={error} />} {notice && <p role="status">{notice}</p>}
      {pending.current && !busy && (
        <button onClick={() => void write(pending.current!.path, {})}>Retry the same save</button>
      )}
      {!model.case.stage ? (
        <form className="form" onSubmit={start}>
          <label>
            Property
            <select name="premises" required disabled={locked}>
              <option value="">Choose a property</option>
              {premises.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.display_name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Request
            <select name="kind" disabled={locked}>
              <option>Sales pack</option>
              <option>Certificate</option>
              <option>Follow-up enquiries</option>
            </select>
          </label>
          <button disabled={locked}>Start preparation</button>
        </form>
      ) : (
        <>
          <p>
            {model.case.stage} · Version {model.case.version}
          </p>
          <label>
            Fee status
            <select value={fee} disabled={locked} onChange={(e) => setFee(e.target.value)}>
              {['Unconfirmed', 'Awaiting payment', 'Paid', 'Waived'].map((s) => (
                <option key={s}>{s}</option>
              ))}
            </select>
          </label>
          <p>Fee status records the case position; it does not post a receipt.</p>
          {answers.map((a, i) => (
            <fieldset key={i} disabled={locked}>
              <legend>Answer {i + 1}</legend>
              <label>
                Question
                <input
                  value={a.question}
                  onChange={(e) => update(i, { question: e.target.value })}
                  maxLength={500}
                />
              </label>
              <label>
                Answer
                <textarea
                  value={a.answer}
                  onChange={(e) => update(i, { answer: e.target.value })}
                  maxLength={5000}
                />
              </label>
              <label>
                Supporting document
                <select
                  value={a.documentId}
                  onChange={(e) => {
                    const d = model.documents.find((d) => d.id === e.target.value);
                    update(i, { documentId: d?.id ?? '', versionId: d?.versionId ?? '' });
                  }}
                >
                  <option value="">Choose current evidence</option>
                  {model.documents.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.title}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Verified on
                <DateOnlyInput value={a.verifiedOn} onChange={value => update(i, {verifiedOn:value})} />
              </label>
              <button
                type="button"
                onClick={() => setAnswers((old) => old.filter((_, j) => i !== j))}
              >
                Remove draft answer
              </button>
            </fieldset>
          ))}
          <button
            disabled={locked || answers.length >= 60}
            onClick={() =>
              setAnswers((old) => [
                ...old,
                {
                  question: '',
                  answer: '',
                  documentId: '',
                  versionId: '',
                  verifiedOn: new Date().toISOString().slice(0, 10),
                },
              ])
            }
          >
            Add answer
          </button>
          <div className="actions">
            <button
              disabled={locked}
              onClick={() =>
                void write('/save', {
                  expectedVersion: model.case.version,
                  feeStatus: fee,
                  stage: 'Draft',
                  answers,
                })
              }
            >
              Save draft
            </button>
            <button
              disabled={locked}
              onClick={() =>
                void write('/save', {
                  expectedVersion: model.case.version,
                  feeStatus: fee,
                  stage: 'Ready',
                  answers,
                })
              }
            >
              Save for review
            </button>
            {model.case.stage === 'Ready' && (
              <button
                disabled={locked}
                onClick={() => void write('/issue', { expectedVersion: model.case.version })}
              >
                Approve and retain issued pack
              </button>
            )}
          </div>
          <p>
            Issued packs are retained below. Saving again starts a revised draft; it does not change
            an issued pack.
          </p>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void write('/follow-up', { note: new FormData(e.currentTarget).get('note') });
            }}
          >
            <label>
              Follow-up enquiry
              <textarea name="note" required minLength={3} maxLength={5000} disabled={locked} />
            </label>
            <button disabled={locked}>Save follow-up</button>
          </form>
          <ConveyancingCompletionAlpha caseId={id} />
          <h3>Issued packs and follow-up</h3>
          {model.history.map((h) => (
            <article key={h.id}>
              <h4>
                {h.event_type}
                {h.detail.version ? ` · Version ${h.detail.version}` : ''}
              </h4>
              <p>{h.detail.note}</p>
              {h.detail.answers && (
                <button onClick={() => void download(h.id)}>Download issued pack</button>
              )}
              {h.detail.answers?.map((a, i) => (
                <div key={i}>
                  <strong>{a.question}</strong>
                  <p>{a.answer}</p>
                  <small>Verified {a.verifiedOn}</small>
                </div>
              ))}
            </article>
          ))}
        </>
      )}
    </section>
  );
}
